import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api } from '../api';
import { HeadActions } from '../components/Layout';
import StatusBoard from '../components/StatusBoard';

const MODES = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'yearly', label: 'Yearly' },
  { key: 'custom', label: 'Custom' },
];

const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' });

const toLocalDate = (value) =>
  new Date(value).toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' });

const rangeForMode = (nextMode, anchor) => {
  const day = new Date(`${anchor}T00:00:00+08:00`);
  if (nextMode === 'weekly') {
    const monday = new Date(day);
    const weekday = (monday.getDay() + 6) % 7;
    monday.setDate(monday.getDate() - weekday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: toLocalDate(monday), end: toLocalDate(sunday) };
  }
  if (nextMode === 'monthly') {
    const start = new Date(day.getFullYear(), day.getMonth(), 1);
    const end = new Date(day.getFullYear(), day.getMonth() + 1, 0);
    return { start: toLocalDate(start), end: toLocalDate(end) };
  }
  if (nextMode === 'yearly') {
    return { start: `${day.getFullYear()}-01-01`, end: `${day.getFullYear()}-12-31` };
  }
  return { start: anchor, end: anchor };
};

const dash = (value) => value || '—';

export default function Uptime() {
  const ctx = useOutletContext();
  const initial = today();
  const [mode, setMode] = useState('daily');
  const [date, setDate] = useState(initial);
  const [startDate, setStartDate] = useState(initial);
  const [endDate, setEndDate] = useState(initial);
  const [summary, setSummary] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('table');
  const [statusPage, setStatusPage] = useState(null);

  const load = async (nextMode = mode, nextDate = date, nextStart = startDate, nextEnd = endDate) => {
    setLoading(true);
    setError('');
    try {
      const [summaryData, reportData, statusData] = await Promise.all([
        api.summary(nextMode, nextDate, nextStart, nextEnd),
        api.report(nextMode, nextDate, nextStart, nextEnd),
        api.statusPage(60).catch(() => null),
      ]);
      setSummary(summaryData);
      setReport(reportData);
      setStatusPage(statusData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(mode, date, startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMode = (next) => {
    if (next === 'custom') {
      setMode(next);
      load(next, startDate, startDate, endDate);
      return;
    }
    const nextRange = rangeForMode(next, date);
    setMode(next);
    setStartDate(nextRange.start);
    setEndDate(nextRange.end);
    load(next, date, nextRange.start, nextRange.end);
  };

  const onRangeChange = (nextStart, nextEnd) => {
    setStartDate(nextStart);
    setEndDate(nextEnd);
    setDate(nextStart);
    setMode('custom');
    if (nextStart && nextEnd && nextStart <= nextEnd) {
      load('custom', nextStart, nextStart, nextEnd);
    }
  };

  const pollNow = async () => {
    setBusy(true);
    try {
      await api.poll();
      await load(mode, date, startDate, endDate);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    setError('');
    try {
      const { blob, filename } = await api.exportUptime(mode, date, startDate, endDate);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const showPeriodStats = mode !== 'daily';

  const cards = useMemo(
    () => [
      { key: 'online', label: 'ONLINE', value: summary?.online ?? '—', sub: 'Cameras online' },
      { key: 'offline', label: 'OFFLINE', value: summary?.offline ?? '—', sub: 'Cameras offline' },
      { key: 'unchecked', label: 'UNCHECKED', value: summary?.unchecked ?? '—', sub: 'Not checked yet' },
      {
        key: 'uptime',
        label: 'UPTIME',
        value: summary ? `${Number(summary.uptimePercent).toFixed(1)}%` : '—',
        sub: summary?.period?.modeLabel || 'Selected period',
        accent: true,
      },
    ],
    [summary]
  );

  const periodCards = useMemo(
    () => [
      {
        key: 'score',
        label: 'SCORE',
        value: report?.summary?.scoreLabel ?? '—',
        sub: 'Time online in this period',
        accent: true,
      },
      {
        key: 'times',
        label: 'TIMES OFFLINE',
        value: report?.summary?.offlineEvents ?? '—',
        sub: 'Total incidents',
      },
      {
        key: 'avg-off',
        label: 'AVG OFFLINE',
        value: report?.summary?.avgOfflineLabel ?? '—',
        sub: 'Average time offline',
      },
      {
        key: 'avg-back',
        label: 'AVG RECOVERY',
        value: report?.summary?.avgOnlineBackLabel ?? '—',
        sub: 'Average time back online',
      },
    ],
    [report]
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>CCTV Uptime</h1>
          <p>Online / offline camera report for the selected period.</p>
        </div>
        <HeadActions {...ctx}>
          <button type="button" className="btn" onClick={exportExcel} disabled={exporting || loading}>
            {exporting ? 'Export…' : 'Export Excel'}
          </button>
          <button type="button" className="btn btn-gold" onClick={pollNow} disabled={busy}>
            {busy ? 'Checking…' : 'Live check'}
          </button>
        </HeadActions>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="stat-row">
        {cards.map((card) => (
          <article key={card.key} className={`stat-card${card.accent ? ' accent' : ''}`}>
            <div className="stat-kicker">{card.label}</div>
            <div className="stat-value">{card.value}</div>
            <div className="stat-sub">{card.sub}</div>
          </article>
        ))}
      </div>
      {showPeriodStats && (
        <div className="stat-row period-stats">
          {periodCards.map((card) => (
            <article key={card.key} className={`stat-card${card.accent ? ' accent' : ''}`}>
              <div className="stat-kicker">{card.label}</div>
              <div className="stat-value">{card.value}</div>
              <div className="stat-sub">{card.sub}</div>
            </article>
          ))}
        </div>
      )}

      <section className="panel">
        <div className="panel-head">
          <div className="tabs">
            {[
              { key: 'table', label: 'Table' },
              { key: 'status', label: 'Status' },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                className={`tab${view === item.key ? ' active' : ''}`}
                onClick={() => setView(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
          {view === 'table' && (
          <div className="tabs">
            {MODES.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`tab${mode === item.key ? ' active' : ''}`}
                onClick={() => onMode(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
          )}
          <div className="toolbar">
            {summary?.liveChecking && <span className="badge badge-live">LIVE CHECKING</span>}
            {view === 'table' && (
              <>
                <label className="muted">
                  Start{' '}
                  <input
                    type="date"
                    className="date-input"
                    value={startDate}
                    onChange={(e) => onRangeChange(e.target.value, endDate)}
                  />
                </label>
                <label className="muted">
                  End{' '}
                  <input
                    type="date"
                    className="date-input"
                    value={endDate}
                    onChange={(e) => onRangeChange(startDate, e.target.value)}
                  />
                </label>
              </>
            )}
          </div>
        </div>

        {loading ? (
          <div className="empty">Loading report…</div>
        ) : view === 'status' ? (
          statusPage?.monitors?.length ? (
            <StatusBoard data={statusPage} />
          ) : (
            <div className="empty">No status data yet. Sync cameras from DSS first.</div>
          )
        ) : !report?.devices?.length ? (
          <div className="empty">
            <div className="empty-icon">◎</div>
            No device data. Sync cameras from DSS first.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>NO.</th>
                  <th>DEVICE / PTZ</th>
                  {!showPeriodStats && <th>TIME OFF</th>}
                  {!showPeriodStats && <th>TIME ONLINE BACK</th>}
                  <th>TOTAL DOWNTIME</th>
                  {showPeriodStats && <th>SCORE</th>}
                  {showPeriodStats && <th>TIMES OFFLINE</th>}
                  {showPeriodStats && <th>AVG OFFLINE</th>}
                  {showPeriodStats && <th>AVG RECOVERY</th>}
                  <th>STATUS</th>
                  <th>REMARK</th>
                </tr>
              </thead>
              <tbody>
                {report.devices.map((row) => (
                  <tr key={row.deviceId}>
                    <td>{row.bil}</td>
                    <td>{row.deviceName}</td>
                    {!showPeriodStats && (
                      <td className={row.timeOffLabel ? 'time-off' : ''}>{dash(row.timeOffLabel)}</td>
                    )}
                    {!showPeriodStats && (
                      <td className={row.timeOnlineBackLabel ? 'time-on' : ''}>
                        {dash(row.timeOnlineBackLabel)}
                      </td>
                    )}
                    <td>{row.totalDowntimeLabel}</td>
                    {showPeriodStats && <td>{row.scoreLabel || '—'}</td>}
                    {showPeriodStats && <td>{row.offlineCount ?? 0}</td>}
                    {showPeriodStats && <td>{row.avgOfflineLabel || '0s'}</td>}
                    {showPeriodStats && <td>{row.avgOnlineBackLabel || '0s'}</td>}
                    <td>
                      <div className="status-cell">
                        <span className={`dot ${row.status.toLowerCase()}`} />
                        {row.status}
                      </div>
                    </td>
                    <td className="muted">{row.remark || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {report?.note && <p className="note">{report.note}</p>}
      </section>
    </>
  );
}
